import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isIOS, isStandalone } from "../lib/platform";
import { supabase } from "../lib/supabase";
import { PushSetup } from "../components/PushSetup";
import { getFirstLeagueId } from "../lib/league";
import { api } from "../lib/api";

// Notification diagnostics, run ON the phone that isn't getting notifications.
//
// Everything the server can see says a push was delivered, so the remaining
// unknowns all live on the device: which service worker is registered, whether
// the browser will display a notification at all, and what OneSignal thinks the
// subscription state is. This page answers all three where the problem is.

interface SwInfo {
  scope: string;
  scriptURL: string;
  state: string;
}

interface StoredSub {
  provider_subscription_id: string;
  status: string;
  platform: string | null;
}

export function Diagnostics() {
  const navigate = useNavigate();
  const [sw, setSw] = useState<SwInfo[] | null>(null);
  const [oneSignal, setOneSignal] = useState<Record<string, string>>({});
  const [liveId, setLiveId] = useState<string>("");
  const [stored, setStored] = useState<StoredSub[] | null>(null);
  const [msg, setMsg] = useState("");
  const [fixing, setFixing] = useState(false);
  const [lastPush, setLastPush] = useState<string | null | "unknown">("unknown");
  const [leagueId, setLeagueId] = useState<string | null>(null);
  const [realTest, setRealTest] = useState("");
  const [sendingReal, setSendingReal] = useState(false);
  // The readings live behind this. A member never sees them; whoever is helping
  // them can open it with five taps on the heading, or a ?debug=1 link.
  const [debug, setDebug] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("debug") === "1"
        || sessionStorage.getItem("push_debug") === "1";
    } catch { return false; }
  });
  const [taps, setTaps] = useState(0);

  async function refresh() {
    setMsg("");
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      setSw(
        regs.map((r) => ({
          scope: r.scope,
          scriptURL: r.active?.scriptURL ?? r.installing?.scriptURL ?? r.waiting?.scriptURL ?? "(none active)",
          state: r.active ? "active" : r.installing ? "installing" : r.waiting ? "waiting" : "unknown",
        }))
      );
    } else {
      setSw([]);
    }

    const os = (window as any).OneSignal;
    const info: Record<string, string> = {};
    let live = "";
    try {
      live = os?.User?.PushSubscription?.id ?? "";
      info["Subscription id"] = live || "(none)";
      info["Opted in"] = String(os?.User?.PushSubscription?.optedIn ?? "unknown");
      info["OneSignal permission"] = String(os?.Notifications?.permission ?? "unknown");
    } catch {
      info["OneSignal"] = "SDK not loaded on this page";
    }
    setOneSignal(info);
    setLiveId(live);

    // What the server will actually send to. A healthy phone and a healthy
    // OneSignal record still deliver nothing if these two ids are different:
    // OneSignal happily reports "delivered" for a stale subscription record that
    // no longer maps to the device in your hand.
    // Did a push ever physically arrive on this device? The service worker writes
    // a timestamp here on every push, whether or not anything got displayed.
    try {
      const cache = await caches.open("push-debug");
      const hit = await cache.match("/last-push");
      setLastPush(hit ? (await hit.json()).at : null);
    } catch {
      setLastPush("unknown");
    }

    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      const { data } = await supabase
        .from("push_subscriptions")
        .select("provider_subscription_id, status, platform")
        .eq("user_id", auth.user.id);
      setStored((data ?? []) as StoredSub[]);
      setLeagueId(await getFirstLeagueId(auth.user.id));
    } else {
      setStored([]);
    }
  }

  // Point the server at the id this device is actually using right now.
  async function relinkThisDevice() {
    setMsg("");
    if (!liveId) { setMsg("This device has no subscription id yet. Turn notifications on first."); return; }
    setFixing(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) { setMsg("Sign in first, then run this again."); return; }
      // Only registers THIS device. It used to revoke every other one first,
      // so fixing a laptop silently unregistered the phone.
      await supabase.from("push_subscriptions").upsert(
        {
          user_id: auth.user.id,
          provider: "onesignal",
          provider_subscription_id: liveId,
          platform: isStandalone() ? "Android/iOS (home screen app)" : "browser",
          status: "active",
          last_confirmed_at: new Date().toISOString(),
        },
        { onConflict: "user_id,provider_subscription_id" }
      );
      setMsg("Relinked. Send yourself a test from the host page now.");
      await refresh();
    } catch (e: any) {
      setMsg(`Could not relink: ${e?.message ?? String(e)}`);
    } finally {
      setFixing(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  // The decisive test. This asks the service worker already on this device to
  // display a notification, with no network and no OneSignal involved. If this
  // appears, the device can show notifications and the problem is in delivery.
  // If it does not appear, nothing OneSignal sends will ever be shown either.
  async function showLocalNotification() {
    setMsg("");
    try {
      if (!("Notification" in window)) { setMsg("This browser has no Notification support at all."); return; }
      if (Notification.permission !== "granted") {
        setMsg(`Permission is "${Notification.permission}", not "granted". Turn notifications on first.`);
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { setMsg("No service worker is registered on this device, so nothing can display a notification."); return; }
      await reg.showNotification("Local test", {
        body: "Shown by this device itself, with no server involved.",
        icon: "/icons/icon-192.png",
        tag: "local-test",
      });
      setMsg("Asked this device to show a notification. If you do not see one, the problem is on the phone, not the server.");
    } catch (e: any) {
      setMsg(`Failed: ${e?.message ?? String(e)}`);
    }
  }

  // The other half of the decisive test. The local test proves the phone CAN
  // display a notification; this proves one actually travels from our server to
  // this device. Together they say which hop is broken, which neither could do
  // alone.
  async function sendRealTestPush() {
    if (!leagueId) { setRealTest("Sign in and join a crew first."); return; }
    setSendingReal(true);
    setRealTest("");
    try {
      const r = await api<{ note?: string }>(`/api/league/admin`, { body: { op: "test-push", leagueId } });
      setRealTest(`${r?.note ?? "Sent."} If nothing shows up, the message is being sent but not displayed.`);
    } catch (e: any) {
      setRealTest(e?.message ?? "Could not send it.");
    } finally {
      setSendingReal(false);
    }
  }

  // An old service worker from earlier testing can keep controlling this scope
  // and swallow pushes. Clearing them lets OneSignal register cleanly again.
  async function clearServiceWorkers() {
    if (!window.confirm("Remove all service workers on this device and reload? You will need to turn notifications on again after.")) return;
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      setMsg("Cleared. Reloading...");
      setTimeout(() => window.location.reload(), 800);
    } catch (e: any) {
      setMsg(`Could not clear: ${e?.message ?? String(e)}`);
    }
  }

  // Tapping the heading five times opens the readings. Nothing on the page
  // advertises it, because this is for whoever is helping somebody whose
  // notifications are broken, not for the person using the app.
  const openDebug = () => {
    const next = taps + 1;
    setTaps(next);
    if (next >= 5) {
      setDebug(true);
      try { sessionStorage.setItem("push_debug", "1"); } catch { /* private mode */ }
    }
  };

  return (
    <div className="app">
      <div className="header">
        <h1 onClick={openDebug}>Notifications</h1>
        <button className="link" onClick={() => navigate("/")}>Home</button>
      </div>

      {/* Turning them on, and proving they work. That is the whole page for
          somebody who is not troubleshooting. */}
      {leagueId ? (
        <PushSetup leagueId={leagueId} onDone={() => refresh()} />
      ) : (
        <div className="card">
          <p className="muted">Join a crew first and notifications set themselves up with it.</p>
        </div>
      )}

      <div className="card">
        <span className="tag">Try it out</span>
        <p>Send yourself one to make sure it reaches this phone.</p>
        <button className="btn primary" disabled={sendingReal || !leagueId} onClick={sendRealTestPush}>
          {sendingReal ? "Sending…" : "Send a test notification"}
        </button>
        <p className="muted">
          Close the app after you tap it. Phones often hold a notification quietly while you are looking
          at the screen.
        </p>
        {realTest && <p className="muted">{realTest}</p>}
      </div>

      {!isIOS() && (
        <div className="card">
          <span className="tag">Nothing showing up on Android?</span>
          <p>
            Android keeps two notification settings that look identical, and both have to be on. With the
            first one off your phone throws the message away before the app ever sees it.
          </p>
          <p className="muted"><b>1.</b> Press and hold the How Y&apos;all Been icon → Site settings → Notifications</p>
          <p className="muted"><b>2.</b> Settings app → Apps → How Y&apos;all Been → Notifications</p>
          <p className="muted">Then turn notifications on again above.</p>
        </div>
      )}

      {debug && (
        <>
          <div className="card live">
            <span className="tag">Diagnostics</span>
            <p className="muted">
              Readings from this phone, for working out why a notification is not arriving.
            </p>
            <button className="btn ghost" onClick={() => {
              setDebug(false); setTaps(0);
              try { sessionStorage.removeItem("push_debug"); } catch { /* private mode */ }
            }}>
              Hide diagnostics
            </button>
          </div>

      <div className="card">
        <span className="tag">This device</span>
        <p className="muted">Permission: <b>{"Notification" in window ? Notification.permission : "not supported"}</b></p>
        <p className="muted">Opened from home screen icon: <b>{isStandalone() ? "yes" : "no"}</b></p>
        <p className="muted">Phone type: <b>{isIOS() ? "iPhone or iPad" : "Android or desktop"}</b></p>
      </div>

      <div className="card">
        <span className="tag">Service workers</span>
        {sw === null && <p className="muted">Checking...</p>}
        {sw?.length === 0 && <p className="error">None registered. Notifications cannot be shown without one.</p>}
        {sw?.map((r) => (
          <p key={r.scope} className="muted" style={{ fontSize: 13, wordBreak: "break-all" }}>
            {r.scriptURL}<br />scope {r.scope} ({r.state})
          </p>
        ))}
        {sw && sw.length > 1 && (
          <p className="error">More than one is registered. They can fight over which handles a push.</p>
        )}
      </div>

      <div className="card">
        <span className="tag">OneSignal</span>
        {Object.entries(oneSignal).map(([k, v]) => (
          <p key={k} className="muted" style={{ fontSize: 13, wordBreak: "break-all" }}>{k}: <b>{v}</b></p>
        ))}
      </div>

      <div className="card">
        <span className="tag">What the server will send to</span>
        {stored === null && <p className="muted">Checking...</p>}
        {stored?.length === 0 && <p className="error">Nothing stored. The server has no device to send to.</p>}
        {/* Owning several devices is normal and healthy, so only the row for the
            phone you are holding gets judged. Every other row used to be printed
            in red as "does NOT match this device", which reads as a fault when it
            is just your other phone sitting there working fine. */}
        {stored?.map((r) => {
          const isThisDevice = r.provider_subscription_id === liveId;
          const active = r.status === "active";
          const color = isThisDevice ? (active ? "var(--good)" : "var(--danger)") : "var(--muted)";
          return (
            <p key={r.provider_subscription_id} className="muted" style={{ fontSize: 13, wordBreak: "break-all" }}>
              <b style={{ color }}>
                {r.platform || "Unlabelled device"}
                {isThisDevice ? " · the phone you're holding" : " · another one of your devices"}
              </b>
              <br />
              {active ? "registered and active" : "retired, nothing is sent here any more"}
              <br />
              <span style={{ fontSize: 11, opacity: 0.55 }}>{r.provider_subscription_id}</span>
            </p>
          );
        })}
        {(stored ?? []).filter((r) => r.status === "active").length > 1 && (
          <p className="muted">
            A notification goes to every active device on this list, so more than one is fine.
          </p>
        )}
        {stored && stored.length > 0 && liveId &&
          !stored.some((r) => r.provider_subscription_id === liveId && r.status === "active") && (
          <>
            <p className="error">
              The server is sending to a different id than this phone is using, which is why OneSignal
              reports a delivery you never see.
            </p>
            <button className="btn primary" disabled={fixing} onClick={relinkThisDevice}>
              {fixing ? "Relinking..." : "Point the server at this device"}
            </button>
          </>
        )}
      </div>

      <div className="card live">
        <span className="tag">Has a push ever reached this phone?</span>
        {lastPush === "unknown" && <p className="muted">Cannot read this on this device.</p>}
        {lastPush === null && (
          <p className="error">
            No push has ever arrived here. The message is not reaching the phone, so nothing could have
            been displayed. That points at the delivery hop, not at the app.
          </p>
        )}
        {typeof lastPush === "string" && lastPush !== "unknown" && (
          <>
            <p className="muted">
              A push reached this phone at <b>{new Date(lastPush).toLocaleString()}</b>.
            </p>
            {/* This page cannot see whether anything was drawn on screen, so it
                must not announce which of these two happened. It used to always
                claim the notification was not displayed, which contradicted the
                person who had just watched one arrive. */}
            <p className="muted">
              <b>If you saw that notification</b>, this phone is working end to end and there is nothing
              to fix.
            </p>
            <p className="muted">
              <b>If you never saw it</b>, the message is getting here but your phone is not putting it on
              screen. That is a notification setting on the phone, not the app.
            </p>
          </>
        )}
        {lastPush === null && (
          <p className="muted">
            This only starts recording once you have opened the app on the current version. Send yourself
            a test below, wait a few seconds, then tap Refresh at the bottom of this page.
          </p>
        )}
      </div>

      <div className="card live">
        <span className="tag">Test 1: can this phone show a notification at all?</span>
        <p>
          This asks your phone to show one by itself, with nothing sent over the internet. If this fails,
          nothing we send will ever appear either.
        </p>
        <button className="btn primary" onClick={showLocalNotification}>Show a notification right now</button>
        {msg && <p className="muted">{msg}</p>}
      </div>

      <div className="card">
        <span className="tag">Last resort</span>
        <p className="muted">
          If an old service worker from earlier testing is still here, it can swallow notifications.
          This removes them all so a clean one can register.
        </p>
        <button className="btn ghost" onClick={clearServiceWorkers}>Clear service workers and reload</button>
        <button className="btn ghost" onClick={refresh}>Refresh this page's readings</button>
      </div>
        </>
      )}
    </div>
  );
}
